import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Check, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The AI Listing Assistant has its OWN build-time flag, independent of the
// global REACT_APP_AI_ENABLED (which gates AI search + the AI usage card). This
// lets us keep the seller listing helper ON while AI search stays OFF.
const LISTING_AI_ENABLED = String(process.env.REACT_APP_AI_LISTING_ENABLED || '').toLowerCase() === 'true';

/**
 * AiListingAssistant — surfaces AI-generated description + taxonomy suggestions
 * for sellers filling in the create-listing form. The seller has to explicitly
 * accept each suggested field; nothing overwrites their typed data silently.
 *
 * Props:
 *   title           — current title from the form (string)
 *   image_url       — first uploaded image (optional, helps the model)
 *   category_hint   — current category if user already picked one
 *   onApply         — callback(suggestion) when the seller hits "Pielietot".
 *                     suggestion shape: { description?, category?, subcategory?, product_type?, title_polished? }
 */
export default function AiListingAssistant({ title, image_url, category_hint, onApply }) {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  if (!LISTING_AI_ENABLED) return null;

  const isLV = language === 'lv';
  const tooShort = !title || title.trim().length < 4;

  const run = async () => {
    if (tooShort || loading) return;
    setLoading(true);
    setError(null);
    setSuggestion(null);
    try {
      const res = await axios.post(`${API}/ai/listing/suggest`, {
        title: title.trim(),
        image_url: image_url || null,
        category_hint: category_hint || null,
      });
      if (!res.data || (!res.data.description && !res.data.category)) {
        setError(isLV ? 'AI neatrada ieteikumu šim virsrakstam.' : 'AI could not generate suggestions.');
      } else {
        setSuggestion(res.data);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setError(detail || (isLV ? 'AI šobrīd nav pieejams' : 'AI unavailable'));
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (suggestion && typeof onApply === 'function') onApply(suggestion);
    setSuggestion(null);
  };

  return (
    <div className="rounded-xl border border-[#7C3AED]/30 bg-gradient-to-br from-[#7C3AED]/5 to-[#D97736]/5 p-3 space-y-2" data-testid="ai-listing-assistant">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#7C3AED]" />
          <span className="text-sm font-medium text-[#121212]">
            {isLV ? 'AI sludinājuma palīgs' : 'AI listing assistant'}
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={run}
          disabled={tooShort || loading}
          className="h-8 bg-[#7C3AED] hover:bg-[#6D28D9] text-white disabled:opacity-50"
          data-testid="ai-listing-suggest-btn"
        >
          {loading ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{isLV ? 'Ģenerē…' : 'Generating…'}</>
          ) : (
            <><Sparkles className="w-3.5 h-3.5 mr-1.5" />{isLV ? 'Ģenerēt aprakstu' : 'Generate'}</>
          )}
        </Button>
      </div>
      {tooShort && !suggestion && !loading && (
        <p className="text-xs text-[#5C5C5C]">
          {isLV ? 'Ievadi vismaz 4 rakstzīmes virsrakstā, lai AI varētu palīdzēt.' : 'Type at least 4 characters in the title to use AI.'}
        </p>
      )}
      {error && (
        <p className="text-xs text-red-600" data-testid="ai-listing-error">{error}</p>
      )}
      {suggestion && (
        <div className="rounded-lg bg-white border border-[#7C3AED]/20 p-3 space-y-2" data-testid="ai-listing-suggestion">
          {suggestion.vision_used && (
            <div className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#7C3AED] bg-[#7C3AED]/10 px-2 py-0.5 rounded-full" data-testid="ai-vision-badge">
              <ImageIcon className="w-3 h-3" />
              {isLV ? 'Attēls analizēts' : 'Image analyzed'}
            </div>
          )}
          {suggestion.title_polished && (
            <div className="text-xs">
              <span className="font-semibold text-[#7C3AED]">{isLV ? 'Uzlabots virsraksts:' : 'Polished title:'}</span>{' '}
              <span className="text-[#121212]">{suggestion.title_polished}</span>
            </div>
          )}
          {suggestion.description && (
            <div className="text-sm text-[#121212] leading-relaxed">
              {suggestion.description}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {suggestion.category && (
              <Badge variant="outline" className="text-xs">{suggestion.category}</Badge>
            )}
            {suggestion.subcategory && (
              <Badge variant="outline" className="text-xs">{suggestion.subcategory}</Badge>
            )}
            {suggestion.product_type && (
              <Badge variant="outline" className="text-xs">{suggestion.product_type}</Badge>
            )}
            {(suggestion.tags || []).map((tag, i) => (
              <Badge key={`tag-${i}`} variant="outline" className="text-xs bg-[#F3F2EE]">#{tag}</Badge>
            ))}
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => setSuggestion(null)} className="h-8 text-[#5C5C5C]" data-testid="ai-listing-dismiss">
              <X className="w-3.5 h-3.5 mr-1" />{isLV ? 'Atcelt' : 'Dismiss'}
            </Button>
            <Button type="button" size="sm" onClick={apply} className="h-8 bg-[#7C3AED] hover:bg-[#6D28D9] text-white" data-testid="ai-listing-apply">
              <Check className="w-3.5 h-3.5 mr-1" />{isLV ? 'Pielietot' : 'Apply'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
